import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createEditor, Editor, Element, Transforms, Text, Range, Point, Path, Node } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import {
  Box, IconButton, Divider, Paper, Tooltip,
  Popover, Typography, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Avatar, Chip, Stack,
  Checkbox, List, ListItemButton, ListItemIcon, ListItemText, CircularProgress, InputAdornment, Popper, MenuList,
} from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatQuote as QuoteIcon,
  FormatListBulleted as BulletIcon,
  FormatListNumbered as NumberIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  BorderAll as TableIcon,
  Comment as CommentIcon,
  CheckCircle as ResolveIcon,
  Reply as ReplyIcon,
  Edit as EditCommentIcon,
  DeleteOutline as DeleteCommentIcon,
  MenuBook as CiteIcon,
  Style as FormatReferencesIcon,
  Search as SearchIcon,
  FormatIndentIncrease as IndentIcon,
  FormatIndentDecrease as OutdentIcon,
  Spellcheck as ThesaurusIcon,
  RateReview as ReviewIcon,
  Check as AcceptIcon,
  Close as RejectIcon,
  ArrowDropDown as MoreIcon,
  AddPhotoAlternate as FigureIcon,
  ChevronRight as SubmenuIcon,
  AddBoxOutlined as InsertIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { markdownToSlateNodes, slateNodesToMarkdown } from 'shared/utils/richTextMarkdown';
import { authorColor, hasChanges, resolveChanges, withTrackChanges } from './trackChanges';
import { fetchSynonyms, matchCase } from './thesaurus';

const DEFAULT_FONT_SIZE = 1.4;
const MIN_FONT_SIZE = 0.85;
const MAX_FONT_SIZE = 2.2;
const FONT_SIZE_STEP = 0.1;

// Highlight colors
const COMMENT_COLOR_ACTIVE = 'rgba(25, 118, 210, 0.22)';
const COMMENT_BORDER_ACTIVE = 'rgba(25, 118, 210, 0.65)';
const COMMENT_COLOR_RESOLVED = 'rgba(25, 118, 210, 0.07)';
const COMMENT_BORDER_RESOLVED = 'rgba(25, 118, 210, 0.2)';

function generateUUID() {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
        (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16));
}

// ---------- withTables plugin -----------------------------------------------

const withTables = editor => {
  const { deleteBackward, deleteForward, insertBreak, normalizeNode } = editor;

  editor.insertBreak = () => {
    const [cell] = Editor.nodes(editor, { match: n => n.type === 'table-cell' });
    if (cell) {
      Transforms.insertText(editor, '\n');
      return;
    }
    insertBreak();
  };

  editor.deleteBackward = unit => {
    const { selection } = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, { match: n => n.type === 'table-cell' });
      if (cell && Point.equals(selection.anchor, Editor.start(editor, cell[1]))) return;
    }
    deleteBackward(unit);
  };

  editor.deleteForward = unit => {
    const { selection } = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, { match: n => n.type === 'table-cell' });
      if (cell && Point.equals(selection.anchor, Editor.end(editor, cell[1]))) return;
    }
    deleteForward(unit);
  };

  editor.normalizeNode = ([node, path]) => {
    if (node.type === 'table') {
      const nextPath = Path.next(path);
      if (!Node.has(editor, nextPath)) {
        Transforms.insertNodes(
          editor,
          { type: 'paragraph', children: [{ text: '' }] },
          { at: nextPath }
        );
        return;
      }
    }
    normalizeNode([node, path]);
  };

  return editor;
};

// ---------- table operations -------------------------------------------------

function tableOps(editor, cellPath) {
  const rowPath = cellPath.slice(0, -1);
  const tablePath = cellPath.slice(0, -2);
  const cellIdx = cellPath[cellPath.length - 1];
  const rowIdx = rowPath[rowPath.length - 1];

  const getTable = () => Node.get(editor, tablePath);
  const blankCell = () => ({ type: 'table-cell', children: [{ text: '' }] });
  const blankRow = cols => ({ type: 'table-row', children: Array.from({ length: cols }, blankCell) });

  return {
    addRowAbove() {
      const table = getTable();
      Transforms.insertNodes(editor, blankRow(table.children[0].children.length), { at: rowPath });
      const fresh = Node.get(editor, tablePath);
      if (fresh.rowHeights) {
        const h = [...fresh.rowHeights];
        h.splice(rowIdx, 0, undefined);
        Transforms.setNodes(editor, { rowHeights: h }, { at: tablePath });
      }
    },
    addRowBelow() {
      const table = getTable();
      Transforms.insertNodes(editor, blankRow(table.children[0].children.length), { at: Path.next(rowPath) });
      const fresh = Node.get(editor, tablePath);
      if (fresh.rowHeights) {
        const h = [...fresh.rowHeights];
        h.splice(rowIdx + 1, 0, undefined);
        Transforms.setNodes(editor, { rowHeights: h }, { at: tablePath });
      }
    },
    addColLeft() {
      const table = getTable();
      for (let ri = 0; ri < table.children.length; ri++) {
        Transforms.insertNodes(editor, blankCell(), { at: [...tablePath, ri, cellIdx] });
      }
      const fresh = Node.get(editor, tablePath);
      if (fresh.colWidths) {
        const w = [...fresh.colWidths];
        w.splice(cellIdx, 0, undefined);
        Transforms.setNodes(editor, { colWidths: w }, { at: tablePath });
      }
    },
    addColRight() {
      const table = getTable();
      for (let ri = 0; ri < table.children.length; ri++) {
        Transforms.insertNodes(editor, blankCell(), { at: [...tablePath, ri, cellIdx + 1] });
      }
      const fresh = Node.get(editor, tablePath);
      if (fresh.colWidths) {
        const w = [...fresh.colWidths];
        w.splice(cellIdx + 1, 0, undefined);
        Transforms.setNodes(editor, { colWidths: w }, { at: tablePath });
      }
    },
    deleteRow() {
      if (getTable().children.length === 1) {
        Transforms.removeNodes(editor, { at: tablePath });
      } else {
        Transforms.removeNodes(editor, { at: rowPath });
        const fresh = Node.get(editor, tablePath);
        if (fresh && fresh.rowHeights) {
          const h = fresh.rowHeights.filter((_, i) => i !== rowIdx);
          Transforms.setNodes(editor, { rowHeights: h }, { at: tablePath });
        }
      }
    },
    deleteCol() {
      const table = getTable();
      const cols = table.children[0].children.length;
      if (cols === 1) {
        Transforms.removeNodes(editor, { at: tablePath });
      } else {
        for (let ri = table.children.length - 1; ri >= 0; ri--) {
          Transforms.removeNodes(editor, { at: [...tablePath, ri, cellIdx] });
        }
        const fresh = Node.get(editor, tablePath);
        if (fresh && fresh.colWidths) {
          const w = fresh.colWidths.filter((_, i) => i !== cellIdx);
          Transforms.setNodes(editor, { colWidths: w }, { at: tablePath });
        }
      }
    },
    deleteTable() {
      Transforms.removeNodes(editor, { at: tablePath });
    },
  };
}

// ---------- insert table helper ---------------------------------------------

function insertTable(editor, rows = 3, cols = 3) {
  const { selection } = editor;
  if (!selection) return;
  const topPath = [selection.anchor.path[0]];
  const tablePath = Path.next(topPath);
  const tableNode = {
    type: 'table',
    children: Array.from({ length: rows }, () => ({
      type: 'table-row',
      children: Array.from({ length: cols }, () => ({
        type: 'table-cell',
        children: [{ text: '' }],
      })),
    })),
  };
  Transforms.insertNodes(editor, tableNode, { at: tablePath });
  Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] }, { at: Path.next(tablePath) });
  Transforms.select(editor, Editor.start(editor, [...tablePath, 0, 0]));
}

// ---------- Leaf renderer ---------------------------------------------------

// ---- In-text citations ---------------------------------------------------
//
// Stored markdown keeps canonical markers such as [1] or [1, 3–5]. Editors that
// receive the `citations` prop turn them into inline void "citation" elements
// (rendered as a formatted chip the caret moves past) and back into markers
// when serialising, so the stored text never changes shape.

const CITE_MARKER_RE = /\[\d+(?:\s*[,–-]\s*\d+)*\]/g;

const withCitations = (editor) => {
  const { isInline, isVoid } = editor;
  editor.isInline = (el) => el.type === 'citation' || isInline(el);
  // Citations (inline) and figure placeholders (block) are not editable as text
  editor.isVoid = (el) => el.type === 'citation' || el.type === 'figure' || isVoid(el);
  return editor;
};

/** Insert a figure placeholder after the block containing the cursor. */
const insertFigurePlaceholder = (editor, { caption, note, figureId }) => {
  const top = editor.selection ? editor.selection.anchor.path[0] : editor.children.length - 1;
  const at = [top + 1];
  Transforms.insertNodes(editor, {
    type: 'figure', caption, note, ...(figureId ? { figureId } : {}), children: [{ text: '' }],
  }, { at });
  // Keep a paragraph after the figure so writing can continue below it
  if (at[0] + 1 >= editor.children.length) {
    Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] }, { at: [at[0] + 1] });
  }
  Transforms.select(editor, Editor.start(editor, [at[0] + 1]));
};

const citationNode = (marker) => ({ type: 'citation', marker, children: [{ text: '' }] });

// Split text leaves around markers into citation elements (marks are kept on the text pieces)
const toCitationNodes = (nodes) => nodes.map((node) => {
  if (!node.children) return node;
  const children = [];
  node.children.forEach((child) => {
    if (typeof child.text !== 'string') { children.push(...toCitationNodes([child])); return; }
    let last = 0;
    for (const match of child.text.matchAll(CITE_MARKER_RE)) {
      children.push({ ...child, text: child.text.slice(last, match.index) });
      children.push(citationNode(match[0]));
      last = match.index + match[0].length;
    }
    children.push({ ...child, text: child.text.slice(last) });
  });
  return { ...node, children };
});

const fromCitationNodes = (nodes) => nodes.map((node) => {
  if (node.type === 'citation') return { text: node.marker };
  return node.children ? { ...node, children: fromCitationNodes(node.children) } : node;
});

const Leaf = ({ attributes, children, leaf, commentMap, onOpenComment }) => {
  if (leaf.bold) children = <strong>{children}</strong>;
  if (leaf.italic) children = <em>{children}</em>;
  if (leaf.underline) children = <u>{children}</u>;

  // Tracked changes: author-coloured; hovering shows who made the change and when
  const change = leaf.del || leaf.ins;
  if (change) {
    const color = authorColor(change.a);
    const when = change.t ? new Date(change.t).toLocaleString() : '';
    const label = [
      leaf.ins && `Inserted by ${leaf.ins.n}`,
      leaf.del && `Deleted by ${leaf.del.n}`,
    ].filter(Boolean).join(' · ');
    children = (
      <Tooltip title={`${label}${when ? ` — ${when}` : ''}`} placement="top" arrow>
        <span
          style={{
            color,
            textDecoration: leaf.del ? 'line-through' : 'underline',
            textDecorationColor: color,
            backgroundColor: leaf.del ? 'transparent' : `${color}1f`,
          }}
        >
          {children}
        </span>
      </Tooltip>
    );
  }

  if (leaf.commentId) {
    const comment = commentMap?.[leaf.commentId];
    const resolved = comment?.resolved ?? false;
    return (
      <span {...attributes}>
        <Tooltip
          title={
            <Box sx={{ maxWidth: 280, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Avatar
                src={comment?.authorAvatar || undefined}
                alt={comment?.authorName}
                sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main', flexShrink: 0, mt: 0.25 }}
              >
                {(comment?.authorName || '?')[0].toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.3 }}>
                  {comment?.authorName || 'Comment'}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{comment?.body || ''}</Typography>
                {comment?.replies?.length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                    {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'} — click to view
                  </Typography>
                )}
                {!comment && (
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>Click to view comment</Typography>
                )}
              </Box>
            </Box>
          }
          arrow
          placement="top"
        >
          <span
            style={{
              backgroundColor: resolved ? COMMENT_COLOR_RESOLVED : COMMENT_COLOR_ACTIVE,
              borderBottom: `2px solid ${resolved ? COMMENT_BORDER_RESOLVED : COMMENT_BORDER_ACTIVE}`,
              cursor: 'pointer',
              borderRadius: 2,
              padding: '1px 0',
            }}
            onMouseDown={(e) => {
              // Only open dialog on a plain click (not text selection drag)
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              onOpenComment?.(leaf.commentId);
            }}
          >
            {children}
          </span>
        </Tooltip>
      </span>
    );
  }

  return <span {...attributes}>{children}</span>;
};

// ---------- CommentThreadDialog ---------------------------------------------

// Edit / delete controls for one message (the comment itself or a reply), shown to its author only
const MessageActions = ({ onEdit, onDelete }) => {
  const [confirming, setConfirming] = useState(false);
  return confirming ? (
    <Box display="flex" alignItems="center" gap={0.5} sx={{ ml: 'auto' }}>
      <Typography variant="caption" color="text.secondary">Delete?</Typography>
      <Button size="small" color="error" onClick={() => { setConfirming(false); onDelete(); }} sx={{ minWidth: 0, py: 0 }}>Delete</Button>
      <Button size="small" onClick={() => setConfirming(false)} sx={{ minWidth: 0, py: 0 }}>Keep</Button>
    </Box>
  ) : (
    <Box display="flex" sx={{ ml: 'auto' }}>
      <Tooltip title="Edit"><IconButton size="small" aria-label="Edit" onClick={onEdit}><EditCommentIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
      <Tooltip title="Delete"><IconButton size="small" aria-label="Delete" onClick={() => setConfirming(true)}><DeleteCommentIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
    </Box>
  );
};

// The text of a message, or a small editor for it while it is being changed
const MessageBody = ({ text, editing, onSave, onCancel, pl }) => {
  const [draft, setDraft] = useState(text);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (editing) setDraft(text); }, [editing, text]);
  if (!editing) return <Typography variant="body2" sx={{ pl, whiteSpace: 'pre-wrap' }}>{text}</Typography>;
  const save = async () => {
    if (!draft.trim() || draft.trim() === text) { onCancel(); return; }
    setBusy(true);
    const ok = await onSave(draft.trim());
    setBusy(false);
    if (ok !== false) onCancel();
  };
  return (
    <Box sx={{ pl }}>
      <TextField fullWidth multiline minRows={2} size="small" autoFocus value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save(); if (e.key === 'Escape') onCancel(); }} />
      <Box display="flex" justifyContent="flex-end" gap={1} mt={0.75}>
        <Button size="small" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button size="small" variant="contained" onClick={save} disabled={busy || !draft.trim()}>Save</Button>
      </Box>
    </Box>
  );
};

const CommentThreadDialog = ({ open, comment, onClose, onResolve, onReply, onEdit, onDelete, onEditReply, currentUserId }) => {
  const [replyBody, setReplyBody] = useState('');
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(null);       // 'comment' | reply index
  useEffect(() => { setEditing(null); }, [comment?.commentId, open]);
  const mine = (authorId) => Boolean(currentUserId && authorId && String(authorId) === String(currentUserId));

  const handleReply = async () => {
    if (!replyBody.trim()) return;
    setReplying(true);
    await onReply?.(replyBody.trim());
    setReplyBody('');
    setReplying(false);
  };

  if (!comment) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 3500 }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <CommentIcon color="primary" fontSize="small" />
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Comment</Typography>
            {comment.resolved && (
              <Chip label="Resolved" size="small" color="success" sx={{ height: 18, fontSize: 11 }} />
            )}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {/* Original comment */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, borderLeft: `3px solid ${COMMENT_BORDER_ACTIVE}` }}>
          <Box display="flex" alignItems="center" gap={1} mb={0.75}>
            <Avatar
              src={comment.authorAvatar || undefined}
              alt={comment.authorName}
              sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'primary.main' }}
            >
              {(comment.authorName || '?')[0].toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>{comment.authorName || 'Unknown'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {comment.created ? new Date(comment.created).toLocaleString() : ''}
              </Typography>
            </Box>
            {onEdit && mine(comment.authorId) && editing !== 'comment' && (
              <MessageActions onEdit={() => setEditing('comment')} onDelete={() => onDelete?.()} />
            )}
          </Box>
          <MessageBody text={comment.body} pl="38px" editing={editing === 'comment'}
            onSave={(body) => onEdit?.(body)} onCancel={() => setEditing(null)} />
        </Box>

        {/* Replies */}
        {(comment.replies || []).length > 0 && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {comment.replies.map((reply, idx) => (
              <Box key={idx} sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                  <Avatar
                    src={reply.author_avatar || undefined}
                    alt={reply.author_name}
                    sx={{ width: 26, height: 26, fontSize: 11 }}
                  >
                    {(reply.author_name || '?')[0].toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>{reply.author_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {reply.created ? new Date(reply.created).toLocaleString() : ''}{reply.edited ? ' · edited' : ''}
                    </Typography>
                  </Box>
                  {onEditReply && mine(reply.author_id) && editing !== idx && (
                    <MessageActions onEdit={() => setEditing(idx)} onDelete={() => onEditReply(idx, null)} />
                  )}
                </Box>
                <MessageBody text={reply.body} pl="34px" editing={editing === idx}
                  onSave={(body) => onEditReply?.(idx, body)} onCancel={() => setEditing(null)} />
              </Box>
            ))}
          </Stack>
        )}

        {/* Reply input */}
        <TextField
          fullWidth
          multiline
          rows={2}
          size="small"
          placeholder="Write a reply..."
          value={replyBody}
          onChange={e => setReplyBody(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleReply(); }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, justifyContent: 'space-between' }}>
        <Button
          size="small"
          startIcon={<ResolveIcon />}
          onClick={() => onResolve?.()}
          color={comment.resolved ? 'inherit' : 'success'}
          variant="outlined"
        >
          {comment.resolved ? 'Reopen' : 'Resolve'}
        </Button>
        <Box display="flex" gap={1}>
          <Button size="small" onClick={onClose}>Close</Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<ReplyIcon />}
            onClick={handleReply}
            disabled={!replyBody.trim() || replying}
          >
            Reply
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const RESIZE_EDGE = 6;

// ---------- SlateEditor component -------------------------------------------

/**
 * Pick one or more references to cite. items: [{ id, label, number }] where
 * number is the existing in-text number for already cited references.
 */
const formatSize = (bytes = 0) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/** Image upload for a figure placeholder: compressed on the server, the original is not kept. */
const FigureUploadField = ({ figure, usage, onUpload, onChange }) => {
  const inputRef = useRef(null);
  const [state, setState] = useState({ busy: false, error: '', original: 0 });
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setState({ busy: true, error: '', original: file.size });
    try {
      const uploaded = await onUpload(file);
      setState({ busy: false, error: '', original: file.size });
      onChange(uploaded.id);
    } catch (err) {
      setState({ busy: false, error: err?.response?.data?.error || 'Upload failed.', original: 0 });
    }
  };
  return (
    <Box sx={{ mb: 2, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
      {figure ? (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <img src={figure.url} alt="" style={{ width: 96, height: 72, objectFit: 'contain', background: '#fafafa' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>{figure.original_name || 'Image'}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Stored as {figure.kind === 'line' ? 'lossless' : 'compressed'} WebP, {formatSize(figure.size_bytes)}
              {state.original > figure.size_bytes ? ` (from ${formatSize(state.original)})` : ''}
            </Typography>
          </Box>
          <Button size="small" onClick={() => inputRef.current?.click()} disabled={state.busy}>Replace</Button>
          <Button size="small" color="error" onClick={() => onChange(null)} disabled={state.busy}>Remove image</Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button variant="outlined" size="small" startIcon={state.busy ? <CircularProgress size={14} /> : <FigureIcon />}
                  onClick={() => inputRef.current?.click()} disabled={state.busy}>
            {state.busy ? 'Compressing…' : 'Upload image'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            PNG, JPEG, TIFF or WebP up to 10 MB. Compressed to WebP (max 1600 px, 400 KB); the original is not kept.
          </Typography>
        </Box>
      )}
      {state.error && <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>{state.error}</Typography>}
      {usage && (
        <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
          Figures in this document: {formatSize(usage.used_bytes)} · your storage: {formatSize(usage.storage_used_bytes ?? usage.used_bytes)} of {formatSize(usage.limit_bytes)}
        </Typography>
      )}
      <input ref={inputRef} type="file" hidden accept="image/png,image/jpeg,image/webp,image/tiff,image/gif,image/bmp" onChange={pick} />
    </Box>
  );
};

const CitationPickerDialog = ({ open, items, onClose, onInsert }) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState([]);
  const [inserting, setInserting] = useState(false);

  React.useEffect(() => { if (open) { setQuery(''); setSelected([]); } }, [open]);

  const q = query.trim().toLowerCase();
  const visible = items.filter(it => !q || it.label.toLowerCase().includes(q));
  const toggle = (id) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const handleInsert = async () => {
    setInserting(true);
    try { await onInsert(selected); } finally { setInserting(false); }
  };

  return (
    <Dialog disableRestoreFocus open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Insert reference</DialogTitle>
      <DialogContent>
        {items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            The bibliography is empty. Add references in the Bibliography tab first.
          </Typography>
        ) : (
          <>
            <TextField
              autoFocus fullWidth size="small" placeholder="Search author, title, year…" value={query}
              onChange={(e) => setQuery(e.target.value)} sx={{ mt: 1, mb: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <List dense sx={{ maxHeight: 420, overflowY: 'auto' }}>
              {visible.map(it => (
                <ListItemButton key={it.id} onClick={() => toggle(it.id)} selected={selected.includes(it.id)} sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" size="small" checked={selected.includes(it.id)} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText
                    primary={it.label}
                    secondary={it.number ? `Cited as [${it.number}]` : 'Not cited yet — will get the next number'}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItemButton>
              ))}
              {visible.length === 0 && (
                <Typography color="text.secondary" sx={{ p: 2 }}>No matching references.</Typography>
              )}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleInsert} disabled={!selected.length || inserting}
          startIcon={inserting ? <CircularProgress size={16} /> : <CiteIcon />}>
          Insert{selected.length > 1 ? ` ${selected.length}` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SlateEditor = ({
  value,
  onChange,
  placeholder,
  height = 300,
  richText = false,
  // Comment props
  comments = [],
  onAddComment,
  onResolveComment,
  onReplyComment,
  // Authors may change or remove their own comment / replies: (commentId, body) -> bool,
  // (commentId) -> bool, (commentId, replyIndex, body | null to delete) -> bool
  onEditComment,
  onDeleteComment,
  onEditReply,
  currentUserId,
  // Track changes: { author: { id, name } } enables the Editing / Reviewing modes
  trackChanges,
  // Keep the formatting toolbar pinned while the surrounding container scrolls
  stickyToolbar = false,
  // Allow figure placeholders (rich text only)
  allowFigures = false,
  // Uploaded figure images: { items: { [id]: { url, kind, size_bytes } }, onUpload(file) → figure }
  figures = null,
  // In-text citations: { items: [{ id, label, number }], onCite: async (ids) => markerText,
  //                      onFormat?: () => void (opens the citation-style picker),
  //                      display?: Map(marker -> formatted citation) shown in place of [n],
  //                      title?: marker -> [full reference strings] shown on hover }
  citations,
}) => {
  const [reviewing, setReviewing] = useState(false);
  // Read by the track-changes plugin on every operation
  const trackCtxRef = useRef(null);
  trackCtxRef.current = { reviewing: Boolean(trackChanges && reviewing), author: trackChanges?.author };
  const editor = useMemo(
    () => withCitations(withTrackChanges(withTables(withHistory(withReact(createEditor()))), trackCtxRef)),
    [],
  );
  const [changeMenu, setChangeMenu] = useState(null);
  const [figureDialog, setFigureDialog] = useState(null); // { path?, caption, note }
  const [thesaurus, setThesaurus] = useState(null); // { anchor | position, word, range, results, loading }
  const citeDisplay = citations?.display;
  // Markdown for onChange, with citation chips turned back into [n] markers
  const serialize = useCallback((nodes) => slateNodesToMarkdown(fromCitationNodes(nodes)), []);

  const initialValue = useMemo(() => {
    if (!value || value === '') return [{ type: 'paragraph', children: [{ text: '' }] }];
    if (Array.isArray(value)) return value;
    if (richText) return citations ? toCitationNodes(markdownToSlateNodes(value)) : markdownToSlateNodes(value);
    return value.split('\n').map(line => ({ type: 'paragraph', children: [{ text: line }] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [slateValue, setSlateValue] = useState(initialValue);
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
  const [tablePickerAnchor, setTablePickerAnchor] = useState(null);
  const [pickerHover, setPickerHover] = useState({ rows: 1, cols: 1 });
  const [contextMenu, setContextMenu] = useState(null); // {x, y, cellPath, hasSelection}
  const [ctxSub, setCtxSub] = useState(null); // open submenu: {key, anchor, focus?}
  const savedSelectionRef = useRef(null);
  const [citeOpen, setCiteOpen] = useState(false);
  const citeSelectionRef = useRef(null);

  // Comment state
  const [addCommentOpen, setAddCommentOpen] = useState(false);
  const [addCommentBody, setAddCommentBody] = useState('');
  const [addCommentSubmitting, setAddCommentSubmitting] = useState(false);
  const [threadOpen, setThreadOpen] = useState(false);
  const [activeCommentId, setActiveCommentId] = useState(null);

  // Build a commentId→comment lookup map for the Leaf renderer
  const commentMap = useMemo(() => {
    const map = {};
    for (const c of (comments || [])) {
      map[c.commentId] = c;
    }
    return map;
  }, [comments]);

  const activeComment = commentMap[activeCommentId] || null;

  const PICKER_ROWS = 6;
  const PICKER_COLS = 8;

  const zoomIn = () => setFontSize(s => Math.min(MAX_FONT_SIZE, +(s + FONT_SIZE_STEP).toFixed(2)));
  const zoomOut = () => setFontSize(s => Math.max(MIN_FONT_SIZE, +(s - FONT_SIZE_STEP).toFixed(2)));

  const handleChange = useCallback((newValue) => {
    setSlateValue(newValue);
    if (richText) onChange(serialize(newValue));
    else onChange(newValue.map(n => Node.string(n)).join('\n'));
  }, [onChange, richText, serialize]);

  // ---- column resize ------------------------------------------------------

  const startColResize = useCallback((e, tablePath, colIdx) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const tableNode = Node.get(editor, tablePath);
    const numCols = tableNode.children[0].children.length;

    const baseWidths = tableNode.colWidths
      ? [...tableNode.colWidths]
      : Array(numCols).fill(undefined);

    const cellEl = e.currentTarget;
    if (baseWidths[colIdx] == null) baseWidths[colIdx] = cellEl.offsetWidth;

    let rafId = null;

    const applyWidthToDOM = (newWidth) => {
      try {
        const tbl = Node.get(editor, tablePath);
        tbl.children.forEach((row) => {
          const cell = row.children[colIdx];
          if (cell) {
            const el = ReactEditor.toDOMNode(editor, cell);
            if (el) el.style.width = `${newWidth}px`;
          }
        });
      } catch (_) {}
    };

    const onMouseMove = (ev) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const newWidth = Math.max(40, baseWidths[colIdx] + ev.clientX - startX);
        applyWidthToDOM(newWidth);
      });
    };

    const onMouseUp = (ev) => {
      if (rafId) cancelAnimationFrame(rafId);
      const newWidth = Math.max(40, baseWidths[colIdx] + ev.clientX - startX);
      const newWidths = [...baseWidths];
      newWidths[colIdx] = newWidth;
      Transforms.setNodes(editor, { colWidths: newWidths }, { at: tablePath });
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [editor]);

  // ---- row resize ---------------------------------------------------------

  const startRowResize = useCallback((e, tablePath, rowIdx) => {
    e.preventDefault();
    e.stopPropagation();

    const startY = e.clientY;
    const tableNode = Node.get(editor, tablePath);
    const numRows = tableNode.children.length;

    const baseHeights = tableNode.rowHeights
      ? [...tableNode.rowHeights]
      : Array(numRows).fill(undefined);

    const cellEl = e.currentTarget;
    if (baseHeights[rowIdx] == null) baseHeights[rowIdx] = cellEl.offsetHeight;

    let rafId = null;

    const applyHeightToDOM = (newHeight) => {
      try {
        const tbl = Node.get(editor, tablePath);
        const row = tbl.children[rowIdx];
        if (row) {
          row.children.forEach((cell) => {
            const el = ReactEditor.toDOMNode(editor, cell);
            if (el) el.style.height = `${newHeight}px`;
          });
        }
      } catch (_) {}
    };

    const onMouseMove = (ev) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const newHeight = Math.max(28, baseHeights[rowIdx] + ev.clientY - startY);
        applyHeightToDOM(newHeight);
      });
    };

    const onMouseUp = (ev) => {
      if (rafId) cancelAnimationFrame(rafId);
      const newHeight = Math.max(28, baseHeights[rowIdx] + ev.clientY - startY);
      const newHeights = [...baseHeights];
      newHeights[rowIdx] = newHeight;
      Transforms.setNodes(editor, { rowHeights: newHeights }, { at: tablePath });
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [editor]);

  // ---- element renderer ---------------------------------------------------

  const renderElement = useCallback(({ attributes, children, element }) => {
    const style = { textAlign: element.align };
    switch (element.type) {

      case 'figure': {
        const image = element.figureId ? figures?.items?.[element.figureId] : null;
        const openFigure = () => setFigureDialog({
          path: ReactEditor.findPath(editor, element), caption: element.caption || '', note: element.note || '',
          figureId: element.figureId || null,
        });
        if (image) {
          return (
            <div {...attributes}>
              <div contentEditable={false} style={{ userSelect: 'none' }}>
                <Box sx={{ my: 1, p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                  <img src={image.url} alt={element.caption || 'Figure'} style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain' }} />
                  {/* Only the caption opens the figure dialog; the image itself is not a click target */}
                  <Typography
                    variant="body2" role="button" tabIndex={0} title="Edit figure" aria-label={`Edit figure: ${element.caption || "no caption"}`}
                    onClick={openFigure}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFigure(); } }}
                    sx={{
                      mt: 0.5, textAlign: 'left', cursor: 'pointer', borderRadius: 0.5, px: 0.5, mx: -0.5,
                      '&:hover, &:focus-visible': { bgcolor: 'action.hover', color: 'primary.main', outline: 'none' },
                    }}
                  >
                    <strong>Caption:</strong> {element.caption || '—'}
                    <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>click to edit</Typography>
                  </Typography>
                </Box>
              </div>
              {children}
            </div>
          );
        }
        return (
          <div {...attributes}>
            <div contentEditable={false} style={{ userSelect: 'none' }}>
              <Box
                onClick={openFigure}
                sx={{
                  border: '2px dashed', borderColor: 'primary.light', borderRadius: 1, p: 2, my: 1,
                  textAlign: 'center', bgcolor: 'action.hover', cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <ImageIcon color="primary" />
                <Typography variant="subtitle2" color="primary.main">Figure placeholder</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}><strong>Caption:</strong> {element.caption || '—'}</Typography>
                {element.note && <Typography variant="caption" color="text.secondary" display="block">{element.note}</Typography>}
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                  {element.figureId
                    ? 'The image is no longer available · click to upload it again'
                    : `${figures?.onUpload ? 'Upload an image or add it' : 'Add the image'} in your local copy · click to edit`}
                </Typography>
              </Box>
            </div>
            {children}
          </div>
        );
      }

      case 'citation':
        return (
          <span {...attributes} contentEditable={false} style={{ userSelect: 'none' }}>
            <Tooltip arrow placement="top" title={(() => {
              const lines = citations?.title?.(element.marker) || [];
              return lines.length
                ? <span style={{ display: 'block', maxWidth: 420, fontSize: 13, lineHeight: 1.45 }}>{lines.map((l) => <span key={l} style={{ display: 'block', margin: '3px 0' }}>{l}</span>)}</span>
                : `Citation ${element.marker}`;
            })()}>
              <span style={{ color: '#1565c0', backgroundColor: 'rgba(21,101,192,0.08)', borderRadius: 3, padding: '0 2px' }}>
                {citeDisplay?.get(element.marker) || element.marker}
              </span>
            </Tooltip>
            {children}
          </span>
        );

      case 'table':
        return (
          <div
            {...attributes}
            style={{
              display: 'table',
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
              width: '100%',
              margin: '6px 0',
            }}
          >
            {children}
          </div>
        );

      case 'table-row':
        return (
          <div {...attributes} style={{ display: 'table-row' }}>
            {children}
          </div>
        );

      case 'table-cell': {
        const cellPath = ReactEditor.findPath(editor, element);
        if (cellPath.length < 3) break;
        const tablePath = cellPath.slice(0, -2);
        const colIdx = cellPath[cellPath.length - 1];
        const rowIdx = cellPath[cellPath.length - 2];
        let tableNode;
        try { tableNode = Node.get(editor, tablePath); } catch (_) { tableNode = {}; }
        const colWidths = tableNode.colWidths || [];
        const rowHeights = tableNode.rowHeights || [];

        const onCellMouseMove = (e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const nearRight  = e.clientX >= r.right  - RESIZE_EDGE;
          const nearBottom = e.clientY >= r.bottom - RESIZE_EDGE;
          e.currentTarget.style.cursor =
            nearRight ? 'col-resize' : nearBottom ? 'row-resize' : '';
        };
        const onCellMouseLeave = (e) => { e.currentTarget.style.cursor = ''; };
        const onCellMouseDown = (e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const nearRight  = e.clientX >= r.right  - RESIZE_EDGE;
          const nearBottom = e.clientY >= r.bottom - RESIZE_EDGE;
          if (nearRight)  { startColResize(e, tablePath, colIdx); return; }
          if (nearBottom) { startRowResize(e, tablePath, rowIdx); return; }
        };

        return (
          <div
            {...attributes}
            onContextMenu={(e) => {
              e.stopPropagation(); // prevent Editable's onContextMenu from firing
              openContextMenu(e, cellPath);
            }}
            onMouseMove={onCellMouseMove}
            onMouseLeave={onCellMouseLeave}
            onMouseDown={onCellMouseDown}
            style={{
              display: 'table-cell',
              border: '1px solid rgba(128,128,128,0.35)',
              padding: '5px 10px',
              verticalAlign: 'top',
              minWidth: 40,
              width: colWidths[colIdx] != null ? `${colWidths[colIdx]}px` : undefined,
              height: rowHeights[rowIdx] != null ? `${rowHeights[rowIdx]}px` : undefined,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              boxSizing: 'border-box',
            }}
          >
            {children}
          </div>
        );
      }

      case 'block-quote':
        return (
          <blockquote
            {...attributes}
            style={{ ...style, borderLeft: '3px solid #ddd', paddingLeft: 16, color: '#666' }}
          >
            {children}
          </blockquote>
        );
      case 'bulleted-list':
        return <ul style={style} {...attributes}>{children}</ul>;
      case 'numbered-list':
        return <ol style={style} {...attributes}>{children}</ol>;
      case 'list-item':
        return <li style={style} {...attributes}>{children}</li>;
      case 'heading-one':
        return <h1 style={style} {...attributes}>{children}</h1>;
      case 'heading-two':
        return <h2 style={style} {...attributes}>{children}</h2>;
      default:
        return (
          <p style={{ ...style, paddingLeft: element.indent ? `${element.indent * 2}em` : undefined }} {...attributes}>
            {children}
          </p>
        );
    }
  }, [editor, startColResize, startRowResize, citeDisplay, citations, figures]);

  const renderLeaf = useCallback(
    (props) => <Leaf {...props} commentMap={commentMap} onOpenComment={(id) => { setActiveCommentId(id); setThreadOpen(true); }} />,
    [commentMap]
  );

  // ---- keyboard handler ---------------------------------------------------

  const handleKeyDown = useCallback((event) => {
    const mod = event.metaKey || event.ctrlKey;
    if (mod && event.key === 'b') { event.preventDefault(); toggleMark('bold'); return; }
    if (mod && event.key === 'i') { event.preventDefault(); toggleMark('italic'); return; }
    if (mod && event.key === 'u') { event.preventDefault(); toggleMark('underline'); return; }

    if (event.key === 'Tab') {
      const [cellEntry] = Editor.nodes(editor, { match: n => n.type === 'table-cell' });
      if (!cellEntry) return;
      event.preventDefault();
      const [, cellPath] = cellEntry;
      const rowPath = cellPath.slice(0, -1);
      const tablePath = cellPath.slice(0, -2);
      const cellIdx = cellPath[cellPath.length - 1];
      const rowIdx = rowPath[rowPath.length - 1];
      const row = Node.get(editor, rowPath);
      const table = Node.get(editor, tablePath);

      if (!event.shiftKey) {
        if (cellIdx < row.children.length - 1) {
          Transforms.select(editor, Editor.start(editor, [...rowPath, cellIdx + 1]));
        } else if (rowIdx < table.children.length - 1) {
          Transforms.select(editor, Editor.start(editor, [...tablePath, rowIdx + 1, 0]));
        } else {
          const newRow = {
            type: 'table-row',
            children: Array.from({ length: row.children.length }, () => ({
              type: 'table-cell', children: [{ text: '' }],
            })),
          };
          Transforms.insertNodes(editor, newRow, { at: [...tablePath, rowIdx + 1] });
          Transforms.select(editor, Editor.start(editor, [...tablePath, rowIdx + 1, 0]));
        }
      } else {
        if (cellIdx > 0) {
          Transforms.select(editor, Editor.start(editor, [...rowPath, cellIdx - 1]));
        } else if (rowIdx > 0) {
          const prevRow = Node.get(editor, [...tablePath, rowIdx - 1]);
          Transforms.select(editor, Editor.start(editor, [...tablePath, rowIdx - 1, prevRow.children.length - 1]));
        }
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      const [tableEntry] = Editor.nodes(editor, { match: n => n.type === 'table' });
      if (tableEntry) {
        const [table, tablePath] = tableEntry;
        const [rowEntry] = Editor.nodes(editor, { match: n => n.type === 'table-row' });
        if (rowEntry && rowEntry[1][rowEntry[1].length - 1] === table.children.length - 1) {
          event.preventDefault();
          const nextPath = Path.next(tablePath);
          if (Node.has(editor, nextPath)) Transforms.select(editor, Editor.start(editor, nextPath));
        }
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      const [tableEntry] = Editor.nodes(editor, { match: n => n.type === 'table' });
      if (tableEntry) {
        const [, tablePath] = tableEntry;
        const [rowEntry] = Editor.nodes(editor, { match: n => n.type === 'table-row' });
        if (rowEntry && rowEntry[1][rowEntry[1].length - 1] === 0) {
          event.preventDefault();
          const tableIdx = tablePath[tablePath.length - 1];
          if (tableIdx > 0) {
            Transforms.select(editor, Editor.end(editor, Path.previous(tablePath)));
          } else {
            Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] }, { at: tablePath });
            Transforms.select(editor, Editor.start(editor, tablePath));
          }
        }
      }
    }
  }, [editor]);

  // ---- mark / block helpers -----------------------------------------------

  const isMarkActive = (format) => Editor.marks(editor)?.[format] === true;
  const toggleMark = (format) => {
    if (isMarkActive(format)) Editor.removeMark(editor, format);
    else Editor.addMark(editor, format, true);
  };
  const isBlockActive = (format) => { const [m] = Editor.nodes(editor, { match: n => n.type === format }); return !!m; };
  const toggleBlock = (format) => {
    const isActive = isBlockActive(format);
    const isList = ['numbered-list', 'bulleted-list'].includes(format);
    Transforms.unwrapNodes(editor, { match: n => ['numbered-list', 'bulleted-list'].includes(n.type), split: true });
    Transforms.setNodes(editor, { type: isActive ? 'paragraph' : isList ? 'list-item' : format });
    if (!isActive && isList) Transforms.wrapNodes(editor, { type: format, children: [] });
  };

  // Paragraph indent (persisted as an indent level on paragraphs)
  const changeIndent = (delta) => {
    const entries = Array.from(Editor.nodes(editor, {
      match: n => Element.isElement(n) && (n.type === 'paragraph' || !n.type) && Editor.isBlock(editor, n),
    }));
    entries.forEach(([node, path]) => {
      const indent = Math.max(0, Math.min(8, (node.indent || 0) + delta));
      if (indent) Transforms.setNodes(editor, { indent }, { at: path });
      else Transforms.unsetNodes(editor, 'indent', { at: path });
    });
  };

  // Thesaurus: the selected word, or the word at the cursor
  const wordRangeAtCursor = () => {
    let sel = editor.selection;
    // Prefer the browser's live selection: Slate syncs it asynchronously after a click
    try {
      const domSelection = window.getSelection();
      if (domSelection?.rangeCount && ReactEditor.hasDOMNode(editor, domSelection.anchorNode)) {
        sel = ReactEditor.toSlateRange(editor, domSelection, { exactMatch: false, suppressThrow: true }) || sel;
      }
    } catch { /* fall back to Slate's selection */ }
    if (!sel) return null;
    if (Range.isExpanded(sel)) return sel;
    const [leaf, path] = Editor.leaf(editor, sel.anchor);
    const text = leaf.text || '';
    let start = sel.anchor.offset;
    let end = start;
    const isWordChar = c => /[\p{L}\p{N}'-]/u.test(c);
    while (start > 0 && isWordChar(text[start - 1])) start -= 1;
    while (end < text.length && isWordChar(text[end])) end += 1;
    if (start === end) return null;
    return { anchor: { path, offset: start }, focus: { path, offset: end } };
  };

  const openThesaurus = async (anchor) => {
    const range = wordRangeAtCursor();
    const word = range ? Editor.string(editor, range).trim() : '';
    if (!word) { setThesaurus({ ...anchor, word: '', range: null, results: [], loading: false }); return; }
    setThesaurus({ ...anchor, word, range, results: [], loading: true });
    try {
      const results = await fetchSynonyms(word);
      setThesaurus(t => (t && t.word === word ? { ...t, results, loading: false } : t));
    } catch {
      setThesaurus(t => (t && t.word === word ? { ...t, results: [], loading: false, error: true } : t));
    }
  };

  const applySynonym = (synonym) => {
    const { range, word } = thesaurus;
    setThesaurus(null);
    if (!range) return;
    Transforms.select(editor, range);
    editor.insertText(matchCase(synonym, word)); // tracked like typing while reviewing
    ReactEditor.focus(editor);
  };

  const MarkButton = ({ format, icon: Icon, tooltip }) => (
    <Tooltip title={tooltip}><span>
      <IconButton onMouseDown={(e) => { e.preventDefault(); toggleMark(format); }}
        sx={{ color: isMarkActive(format) ? 'primary.main' : 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
        <Icon />
      </IconButton>
    </span></Tooltip>
  );

  const BlockButton = ({ format, icon: Icon, tooltip }) => (
    <Tooltip title={tooltip}><span>
      <IconButton onMouseDown={(e) => { e.preventDefault(); toggleBlock(format); }}
        sx={{ color: isBlockActive(format) ? 'primary.main' : 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
        <Icon />
      </IconButton>
    </span></Tooltip>
  );

  // ---- comment actions ----------------------------------------------------

  const handleAddCommentSubmit = async () => {
    if (!addCommentBody.trim()) return;
    setAddCommentSubmitting(true);
    // Restore selection and apply the commentId mark
    const uuid = generateUUID();
    if (savedSelectionRef.current) {
      try {
        Transforms.select(editor, savedSelectionRef.current);
        Editor.addMark(editor, 'commentId', uuid);
        // Trigger onChange so the commentId is serialized into the markdown body
        const newValue = editor.children;
        if (richText) onChange(serialize(newValue));
        else onChange(newValue.map(n => Node.string(n)).join('\n'));
      } catch (_) {}
    }
    await onAddComment?.(uuid, addCommentBody.trim());
    setAddCommentBody('');
    setAddCommentOpen(false);
    setAddCommentSubmitting(false);
  };

  // ---- context menu -------------------------------------------------------

  const closeContext = () => { setContextMenu(null); setCtxSub(null); };

  // Right-click opens the editor menu; Ctrl/Shift + right-click keeps the browser's own menu
  const openContextMenu = (e, cellPath) => {
    if (e.ctrlKey || e.shiftKey || e.metaKey) return;
    e.preventDefault();
    const sel = editor.selection;
    if (!sel || Range.isCollapsed(sel)) {
      // Move the cursor to where the user clicked
      try {
        const range = ReactEditor.findEventRange(editor, e);
        if (range) Transforms.select(editor, range);
      } catch { /* keep the current selection */ }
    }
    savedSelectionRef.current = editor.selection;
    const hasSelection = Boolean(editor.selection && Range.isExpanded(editor.selection));
    setCtxSub(null);
    setContextMenu({ x: e.clientX, y: e.clientY, cellPath, hasSelection });
  };

  // Run a menu action against the selection the menu was opened on
  const ctxRun = (fn) => () => {
    closeContext();
    try {
      if (savedSelectionRef.current) Transforms.select(editor, savedSelectionRef.current);
      ReactEditor.focus(editor);
    } catch { /* selection no longer valid */ }
    fn();
  };

  const saveFigure = () => {
    const { path, caption, note, figureId } = figureDialog;
    const props = { caption: caption.trim(), note: note.trim(), ...(figureId ? { figureId } : {}) };
    if (path) {
      Transforms.setNodes(editor, props, { at: path });
      if (!figureId) Transforms.unsetNodes(editor, 'figureId', { at: path });
    } else {
      if (savedSelectionRef.current) {
        try { Transforms.select(editor, savedSelectionRef.current); } catch { /* insert at the end */ }
      }
      insertFigurePlaceholder(editor, props);
    }
    // Return to the editor once the dialog has closed, keeping the cursor below the figure
    const target = editor.selection;
    setFigureDialog(null);
    setTimeout(() => {
      try {
        ReactEditor.focus(editor);
        if (target) Transforms.select(editor, target);
      } catch { /* editor unmounted */ }
    }, 0);
  };

  const removeFigure = () => {
    Transforms.removeNodes(editor, { at: figureDialog.path });
    setFigureDialog(null);
  };
  const ctxOp = (fn) => { closeContext(); if (contextMenu?.cellPath) fn(tableOps(editor, contextMenu.cellPath)); };

  const handleOpenAddComment = () => {
    closeContext();
    setAddCommentBody('');
    setAddCommentOpen(true);
  };

  const hasCellPath = Boolean(contextMenu?.cellPath);
  const ctxHasSelection = Boolean(contextMenu?.hasSelection);

  // Right-click menu groups (submenus); each item runs against the selection the menu opened on
  const ctxGroups = [
    { key: 'format', label: 'Format', icon: BoldIcon, items: [
      { label: 'Bold', icon: BoldIcon, onClick: ctxRun(() => toggleMark('bold')) },
      { label: 'Italic', icon: ItalicIcon, onClick: ctxRun(() => toggleMark('italic')) },
      { label: 'Underline', icon: UnderlineIcon, onClick: ctxRun(() => toggleMark('underline')) },
    ] },
    { key: 'paragraph', label: 'Paragraph', icon: BulletIcon, items: [
      { label: 'Bulleted list', icon: BulletIcon, onClick: ctxRun(() => toggleBlock('bulleted-list')) },
      { label: 'Numbered list', icon: NumberIcon, onClick: ctxRun(() => toggleBlock('numbered-list')) },
      { label: 'Quote', icon: QuoteIcon, onClick: ctxRun(() => toggleBlock('block-quote')) },
      ...(richText ? [
        { divider: true },
        { label: 'Increase indent', icon: IndentIcon, onClick: ctxRun(() => changeIndent(1)) },
        { label: 'Decrease indent', icon: OutdentIcon, onClick: ctxRun(() => changeIndent(-1)) },
      ] : []),
    ] },
    { key: 'insert', label: 'Insert', icon: InsertIcon, items: [
      ...(citations ? [{ label: 'Reference…', icon: CiteIcon, onClick: () => {
        closeContext(); citeSelectionRef.current = savedSelectionRef.current; setCiteOpen(true);
      } }] : []),
      ...(allowFigures && richText ? [{ label: 'Figure…', icon: FigureIcon, onClick: () => {
        closeContext(); setFigureDialog({ caption: '', note: '' });
      } }] : []),
      ...(!hasCellPath ? [{ label: 'Table (3 × 3)', icon: TableIcon, onClick: ctxRun(() => insertTable(editor, 3, 3)) }] : []),
    ] },
    citations?.onFormat && { key: 'references', label: 'References', icon: FormatReferencesIcon, items: [
      { label: 'Format references…', icon: FormatReferencesIcon, onClick: () => { closeContext(); citations.onFormat(); } },
    ] },
    trackChanges && { key: 'review', label: 'Review', icon: ReviewIcon, items: [
      { label: 'Accept change', icon: AcceptIcon, color: 'success.main', onClick: ctxRun(() => resolveChanges(editor, true)) },
      { label: 'Reject change', icon: RejectIcon, color: 'error.main', onClick: ctxRun(() => resolveChanges(editor, false)) },
      { divider: true },
      { label: reviewing ? 'Switch to Editing' : 'Switch to Reviewing', icon: ReviewIcon, onClick: ctxRun(() => setReviewing(r => !r)) },
    ] },
    hasCellPath && { key: 'table', label: 'Table', icon: TableIcon, items: [
      { label: 'Add row above', onClick: () => ctxOp(ops => ops.addRowAbove()) },
      { label: 'Add row below', onClick: () => ctxOp(ops => ops.addRowBelow()) },
      { label: 'Add column left', onClick: () => ctxOp(ops => ops.addColLeft()) },
      { label: 'Add column right', onClick: () => ctxOp(ops => ops.addColRight()) },
      { divider: true },
      { label: 'Delete row', danger: true, onClick: () => ctxOp(ops => ops.deleteRow()) },
      { label: 'Delete column', danger: true, onClick: () => ctxOp(ops => ops.deleteCol()) },
      { label: 'Delete table', danger: true, onClick: () => ctxOp(ops => ops.deleteTable()) },
    ] },
  ].filter(g => g && g.items.length);

  // ---- render -------------------------------------------------------------


  return (
    <Paper variant="outlined" sx={{ border: 1, borderColor: 'divider' }}>
      <Box sx={{
        borderBottom: 1, borderColor: 'divider', p: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', bgcolor: 'grey.50',
        ...(stickyToolbar && { position: 'sticky', top: 0, zIndex: 2 }),
      }}>
        <MarkButton format="bold" icon={BoldIcon} tooltip="Bold (Ctrl+B / ⌘B)" />
        <MarkButton format="italic" icon={ItalicIcon} tooltip="Italic (Ctrl+I / ⌘I)" />
        <MarkButton format="underline" icon={UnderlineIcon} tooltip="Underline (Ctrl+U / ⌘U)" />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <BlockButton format="block-quote" icon={QuoteIcon} tooltip="Quote" />
        <BlockButton format="bulleted-list" icon={BulletIcon} tooltip="Bulleted list" />
        <BlockButton format="numbered-list" icon={NumberIcon} tooltip="Numbered list" />
        {richText && (
          <>
            <Tooltip title="Decrease indent"><span>
              <IconButton onMouseDown={(e) => { e.preventDefault(); changeIndent(-1); }} sx={{ color: 'text.secondary' }} aria-label="Decrease indent">
                <OutdentIcon />
              </IconButton>
            </span></Tooltip>
            <Tooltip title="Increase indent"><span>
              <IconButton onMouseDown={(e) => { e.preventDefault(); changeIndent(1); }} sx={{ color: 'text.secondary' }} aria-label="Increase indent">
                <IndentIcon />
              </IconButton>
            </span></Tooltip>
          </>
        )}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Synonyms for the selected word"><span>
          <IconButton
            onMouseDown={(e) => { e.preventDefault(); openThesaurus({ anchor: e.currentTarget }); }}
            sx={{ color: thesaurus ? 'primary.main' : 'text.secondary' }}
            aria-label="Synonyms"
          >
            <ThesaurusIcon />
          </IconButton>
        </span></Tooltip>

        {citations && (
          <>
            <Tooltip title="Insert reference">
              <span>
                <IconButton
                  onMouseDown={(e) => { e.preventDefault(); citeSelectionRef.current = editor.selection; setCiteOpen(true); }}
                  sx={{ color: citeOpen ? 'primary.main' : 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
                  aria-label="Insert reference"
                >
                  <CiteIcon />
                </IconButton>
              </span>
            </Tooltip>
            {citations.onFormat && (
              <Tooltip title="Format references">
                <span>
                  <IconButton
                    onMouseDown={(e) => { e.preventDefault(); citations.onFormat(); }}
                    sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
                    aria-label="Format references"
                  >
                    <FormatReferencesIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          </>
        )}

        {allowFigures && richText && (
          <Tooltip title="Insert figure"><span>
            <IconButton
              onMouseDown={(e) => { e.preventDefault(); savedSelectionRef.current = editor.selection; setFigureDialog({ caption: '', note: '' }); }}
              sx={{ color: 'text.secondary' }} aria-label="Insert figure"
            >
              <FigureIcon />
            </IconButton>
          </span></Tooltip>
        )}

        <Tooltip title="Insert table">
          <span>
            <IconButton
              onMouseDown={(e) => { e.preventDefault(); setTablePickerAnchor(e.currentTarget); setPickerHover({ rows: 1, cols: 1 }); }}
              sx={{ color: tablePickerAnchor ? 'primary.main' : 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <TableIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Popover
          open={Boolean(tablePickerAnchor)}
          anchorEl={tablePickerAnchor}
          onClose={() => setTablePickerAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableRestoreFocus
          PaperProps={{ sx: { p: 1.25, userSelect: 'none' } }}
        >
          <Box
            sx={{ display: 'grid', gridTemplateColumns: `repeat(${PICKER_COLS}, 18px)`, gap: '3px', mb: 0.75 }}
            onMouseLeave={() => setPickerHover({ rows: 0, cols: 0 })}
          >
            {Array.from({ length: PICKER_ROWS }, (_, ri) =>
              Array.from({ length: PICKER_COLS }, (_, ci) => {
                const active = ri < pickerHover.rows && ci < pickerHover.cols;
                return (
                  <Box key={`${ri}-${ci}`}
                    onMouseEnter={() => setPickerHover({ rows: ri + 1, cols: ci + 1 })}
                    onMouseDown={(e) => { e.preventDefault(); setTablePickerAnchor(null); insertTable(editor, ri + 1, ci + 1); }}
                    sx={{
                      width: 18, height: 18, border: '1px solid',
                      borderColor: active ? 'primary.main' : 'divider',
                      bgcolor: active ? 'primary.light' : 'transparent',
                      cursor: 'pointer', borderRadius: '2px', opacity: active ? 0.85 : 1,
                    }}
                  />
                );
              })
            )}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', lineHeight: 1 }}>
            {pickerHover.rows > 0 ? `${pickerHover.rows} × ${pickerHover.cols} table` : 'Select table size'}
          </Typography>
        </Popover>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {trackChanges && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Tooltip title={reviewing ? 'Reviewing: your edits are tracked. Click to stop tracking.' : 'Editing: changes are not tracked. Click to track your edits.'}>
              <Button
                size="small"
                variant={reviewing ? 'contained' : 'outlined'}
                color={reviewing ? 'warning' : 'inherit'}
                startIcon={<ReviewIcon />}
                onMouseDown={(e) => { e.preventDefault(); setReviewing(r => !r); }}
                sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'center', height: 30 }}
              >
                {reviewing ? 'Reviewing' : 'Editing'}
              </Button>
            </Tooltip>
            <Tooltip title="Accept change (at cursor or in selection)"><span>
              <IconButton onMouseDown={(e) => { e.preventDefault(); resolveChanges(editor, true); }} sx={{ color: 'success.main' }} aria-label="Accept change">
                <AcceptIcon />
              </IconButton>
            </span></Tooltip>
            <Tooltip title="Reject change (at cursor or in selection)"><span>
              <IconButton onMouseDown={(e) => { e.preventDefault(); resolveChanges(editor, false); }} sx={{ color: 'error.main' }} aria-label="Reject change">
                <RejectIcon />
              </IconButton>
            </span></Tooltip>
            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); setChangeMenu(e.currentTarget); }} aria-label="All changes">
              <MoreIcon />
            </IconButton>
            <Menu anchorEl={changeMenu} open={Boolean(changeMenu)} onClose={() => setChangeMenu(null)}>
              <MenuItem dense disabled={!hasChanges(editor)} onClick={() => { resolveChanges(editor, true, true); setChangeMenu(null); }}>Accept all changes</MenuItem>
              <MenuItem dense disabled={!hasChanges(editor)} onClick={() => { resolveChanges(editor, false, true); setChangeMenu(null); }}>Reject all changes</MenuItem>
            </Menu>
          </>
        )}
        <Tooltip title="Decrease text size"><span>
          <IconButton onMouseDown={(e) => e.preventDefault()} onClick={zoomOut} disabled={fontSize <= MIN_FONT_SIZE}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
            <ZoomOutIcon />
          </IconButton>
        </span></Tooltip>
        <Tooltip title="Increase text size"><span>
          <IconButton onMouseDown={(e) => e.preventDefault()} onClick={zoomIn} disabled={fontSize >= MAX_FONT_SIZE}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
            <ZoomInIcon />
          </IconButton>
        </span></Tooltip>
      </Box>

      <Box sx={{ p: 2, minHeight: height, maxHeight: height * 1.5, overflow: 'auto' }}>
        <Slate editor={editor} initialValue={slateValue} onChange={handleChange}>
          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            spellCheck
            onContextMenu={(e) => openContextMenu(e, null)}
            style={{
              minHeight: height - 50,
              outline: 'none',
              fontFamily: 'inherit',
              fontSize: `${fontSize}rem`,
              lineHeight: 1.6,
            }}
          />
        </Slate>
      </Box>

      {/* Context menu — PaperProps.onMouseDown e.preventDefault() keeps editor focus so the
          selection stays highlighted while the menu is open. click events still fire normally.
          Related actions are grouped into submenus that open on hover, click or →. */}
      <Menu
        open={Boolean(contextMenu)}
        onClose={closeContext}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
        disableEnforceFocus   // keyboard focus may move into the submenu
        slotProps={{ paper: { onMouseDown: (e) => e.preventDefault(), sx: { minWidth: 200 } } }}
      >
        <MenuItem dense onMouseEnter={() => setCtxSub(null)} onClick={() => {
          const pos = { position: { top: contextMenu.y, left: contextMenu.x } };
          ctxRun(() => openThesaurus(pos))();
        }}>
          <ThesaurusIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          Synonyms…
        </MenuItem>
        {ctxHasSelection && onAddComment && (
          <MenuItem dense onMouseEnter={() => setCtxSub(null)} onClick={handleOpenAddComment}>
            <CommentIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
            Add Comment
          </MenuItem>
        )}
        <Divider />
        {ctxGroups.map((group) => (
          <MenuItem
            key={group.key} dense selected={ctxSub?.key === group.key}
            onMouseEnter={(e) => setCtxSub({ key: group.key, anchor: e.currentTarget })}
            onClick={(e) => setCtxSub({ key: group.key, anchor: e.currentTarget })}
            onKeyDown={(e) => { if (e.key === 'ArrowRight') { e.preventDefault(); setCtxSub({ key: group.key, anchor: e.currentTarget, focus: true }); } }}
            aria-haspopup="menu" aria-expanded={ctxSub?.key === group.key}
          >
            <group.icon fontSize="small" sx={{ mr: 1 }} />
            <Box component="span" sx={{ flex: 1 }}>{group.label}</Box>
            <SubmenuIcon fontSize="small" sx={{ ml: 2, color: 'text.secondary' }} />
          </MenuItem>
        ))}
      </Menu>

      {/* Submenu of the hovered group: a Popper rather than a second Menu, so the main menu stays
          interactive (a second modal would hide it) and moving back to it switches groups. */}
      <Popper
        open={Boolean(contextMenu && ctxSub)}
        anchorEl={ctxSub?.anchor}
        placement="right-start"
        sx={{ zIndex: 10000 }}   // just above menus (9999 in the theme), below snackbars and tooltips
      >
        <Paper elevation={0} onMouseDown={(e) => e.preventDefault()} sx={{ minWidth: 210, border: 1, borderColor: 'divider', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)' }}>
          <MenuList
            dense role="menu" autoFocusItem={Boolean(ctxSub?.focus)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') { e.preventDefault(); const anchor = ctxSub?.anchor; setCtxSub(null); anchor?.focus(); }
              if (e.key === 'Escape') closeContext();
            }}
          >
            {(ctxGroups.find((g) => g.key === ctxSub?.key)?.items || []).map((item, i) => (item.divider
              ? <Divider key={`d${i}`} />
              : (
                <MenuItem key={item.label} dense onClick={item.onClick} sx={item.danger ? { color: 'error.main' } : undefined}>
                  {item.icon && <item.icon fontSize="small" sx={{ mr: 1, color: item.color }} />}
                  {item.label}
                </MenuItem>
              )))}
          </MenuList>
        </Paper>
      </Popper>

      {/* Thesaurus suggestions */}
      <Popover
        open={Boolean(thesaurus)}
        onClose={() => setThesaurus(null)}
        anchorEl={thesaurus?.anchor}
        anchorReference={thesaurus?.position ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={thesaurus?.position}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disableRestoreFocus
        PaperProps={{ sx: { p: 1.5, maxWidth: 340 } }}
      >
        {!thesaurus?.word ? (
          <Typography variant="body2" color="text.secondary">Select a word (or place the cursor in one) to see synonyms.</Typography>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary">Synonyms for “{thesaurus.word}”</Typography>
            {thesaurus.loading ? (
              <Box sx={{ py: 1 }}><CircularProgress size={18} /></Box>
            ) : thesaurus.results?.length ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {thesaurus.results.map(w => (
                  <Chip key={w} label={w} size="small" clickable onClick={() => applySynonym(w)} />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {thesaurus.error ? 'The thesaurus service is unavailable.' : 'No suggestions found.'}
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>English suggestions by Datamuse</Typography>
          </>
        )}
      </Popover>

      {/* Figure placeholder dialog */}
      <Dialog open={Boolean(figureDialog)} onClose={() => setFigureDialog(null)} maxWidth="sm" fullWidth disableRestoreFocus sx={{ zIndex: 3500 }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <FigureIcon color="primary" fontSize="small" />
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {figureDialog?.path ? 'Edit figure' : 'Insert figure'}
            </Typography>
          </Box>
        </DialogTitle>
        {figureDialog && (
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Figures are numbered in the order they appear.{' '}
              {figures?.onUpload
                ? 'Upload an image, or leave a placeholder (“Insert Figure N here” in the Word file) and add the image in your local copy.'
                : 'The exported Word file shows an “Insert Figure N here” box with the caption.'}
            </Typography>
            {figures?.onUpload && (
              <FigureUploadField
                figure={figureDialog.figureId ? figures.items?.[figureDialog.figureId] : null}
                usage={figures.usage}
                onUpload={figures.onUpload}
                onChange={(figureId) => setFigureDialog(d => ({ ...d, figureId }))}
              />
            )}
            <TextField
              autoFocus fullWidth multiline minRows={2} size="small" label="Caption" required
              placeholder="e.g. Expression of gene X across tissues. Bars show mean ± SD (n = 3)."
              value={figureDialog.caption}
              onChange={e => setFigureDialog(d => ({ ...d, caption: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth multiline minRows={2} size="small" label="Note to authors (optional)"
              placeholder="e.g. Panel B from Ayşe's microscopy, 300 dpi TIFF in the shared drive"
              value={figureDialog.note}
              onChange={e => setFigureDialog(d => ({ ...d, note: e.target.value }))}
            />
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {figureDialog?.path && (
            <Button size="small" color="error" onClick={removeFigure} sx={{ mr: 'auto' }}>Remove</Button>
          )}
          <Button size="small" onClick={() => setFigureDialog(null)}>Cancel</Button>
          <Button size="small" variant="contained" onClick={saveFigure} disabled={!figureDialog?.caption.trim()}>
            {figureDialog?.path ? 'Save' : 'Insert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add comment dialog */}
      <Dialog open={addCommentOpen} onClose={() => setAddCommentOpen(false)} maxWidth="xs" fullWidth sx={{ zIndex: 3500 }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CommentIcon color="primary" fontSize="small" />
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Add Comment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder="Write your comment..."
            value={addCommentBody}
            onChange={e => setAddCommentBody(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddCommentSubmit(); }}
            sx={{ mt: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Tip: Ctrl+Enter / ⌘+Enter to submit
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button size="small" onClick={() => setAddCommentOpen(false)}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<CommentIcon />}
            onClick={handleAddCommentSubmit}
            disabled={!addCommentBody.trim() || addCommentSubmitting}
          >
            Comment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comment thread dialog */}
      <CommentThreadDialog
        open={threadOpen}
        comment={activeComment}
        onClose={() => { setThreadOpen(false); setActiveCommentId(null); }}
        onResolve={async () => {
          await onResolveComment?.(activeCommentId);
          setThreadOpen(false);
          setActiveCommentId(null);
        }}
        onReply={async (body) => {
          await onReplyComment?.(activeCommentId, body);
        }}
        currentUserId={currentUserId}
        onEdit={onEditComment ? (body) => onEditComment(activeCommentId, body) : undefined}
        onDelete={onDeleteComment ? async () => {
          const id = activeCommentId;
          const ok = await onDeleteComment(id);
          if (ok === false) return;
          // the highlight goes with the comment; the section saves it like any other edit
          Transforms.unsetNodes(editor, 'commentId', { at: [], mode: 'all', match: (n) => Text.isText(n) && n.commentId === id });
          setThreadOpen(false);
          setActiveCommentId(null);
        } : undefined}
        onEditReply={onEditReply ? (index, body) => onEditReply(activeCommentId, index, body) : undefined}
      />
      {citations && (
        <CitationPickerDialog
          open={citeOpen}
          items={citations.items || []}
          onClose={() => setCiteOpen(false)}
          onInsert={async (ids) => {
            const marker = await citations.onCite(ids);
            setCiteOpen(false);
            if (!marker) return;
            // Put the citation where the cursor was (or at the end if the editor was never focused)
            const at = citeSelectionRef.current || Editor.end(editor, []);
            Transforms.select(editor, at);
            Transforms.collapse(editor, { edge: 'end' });
            Transforms.insertNodes(editor, citationNode(marker));
            Transforms.move(editor); // caret after the chip
            // Refocus once the dialog has closed, keeping the caret after the citation
            // (otherwise focus returns to the old caret position and typing lands there)
            const target = editor.selection;
            setTimeout(() => {
              try {
                ReactEditor.focus(editor);
                if (target) Transforms.select(editor, target);
              } catch { /* editor unmounted */ }
            }, 0);
          }}
        />
      )}
    </Paper>
  );
};

export default SlateEditor;
